// File: frontend/src/pages/reports/FeeReport.jsx

import React, { useState, useEffect } from 'react';
import { useSchool } from '../../context/SchoolContext';
import { exportToPDF } from '../../utils/pdfExport';
import api from '../../utils/axios';
import PageHeader from '../../components/PageHeader';

const FeeReportPage = () => {
  const { school } = useSchool();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get('/api/v1/reports/fees', {
          params: { month: selectedMonth, year: selectedYear }
        });
        if (res.data?.success) {
          setData(res.data.data);
        } else {
          setError(res.data?.message || 'Failed to load fee report.');
        }
      } catch (err) {
        console.error('Error loading fee report:', err);
        setError(err.response?.data?.message || err.message || 'Error loading fee report.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [selectedMonth, selectedYear]);

  /**
   * Export Fee Report to PDF
   */
  const handleExportPDF = () => {
    if (!data || !data.monthly_breakdown) return;

    const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const title = `Fee Collection & Revenue Report - ${selectedYear}`;
    const headers = ['Month', 'Total Bills', 'Collected (Rs.)', 'Pending (Rs.)', 'Collection %'];
    const rows = data.monthly_breakdown.map((r) => {
      const collected = parseFloat(r.collected_amount || 0);
      const pending = parseFloat(r.pending_amount || 0);
      const total = collected + pending;
      const pct = total > 0 ? Math.round((collected / total) * 100) : 100;
      return [
        `${monthNames[r.month]} ${r.year}`,
        r.total_bills || 0,
        `Rs. ${collected.toLocaleString()}`,
        `Rs. ${pending.toLocaleString()}`,
        `${pct}%`
      ];
    });

    exportToPDF(title, headers, rows, school?.name);
  };

  const summary = data?.summary || { total_collected: 0, total_pending: 0, defaulters_count: 0 };
  const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Page Navigation Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <PageHeader
          title="Financial Collection Report"
          subtitle="Analyze revenue collections, monthly breakdowns & outstanding balances"
          breadcrumbs={[
            { label: 'Reports', path: '/reports' },
            { label: 'Financial Report' }
          ]}
          backUrl="/reports"
        >
          <button
            onClick={handleExportPDF}
            disabled={loading || !data}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-4 py-2 rounded-xl text-xs shadow-sm transition disabled:opacity-50 flex items-center gap-1.5"
          >
            📄 Export PDF Report
          </button>
        </PageHeader>
      </div>

      {/* Main Body */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8 space-y-8">
        
        {/* Month & Year Select Filters */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h2 className="font-bold text-slate-900 text-base">Select Report Period</h2>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
              className="px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
            >
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              className="px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
            >
              <option value="2025">2025</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-2xl text-sm font-semibold">
            {error}
          </div>
        )}

        {/* 3 Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Total Collected</span>
            <span className="text-3xl font-black text-emerald-600">Rs. {loading ? '...' : parseFloat(summary.total_collected || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}</span>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Pending Balance</span>
            <span className="text-3xl font-black text-red-600">Rs. {loading ? '...' : parseFloat(summary.total_pending || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}</span>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Fee Defaulters</span>
            <span className="text-3xl font-black text-amber-600">{loading ? '...' : summary.defaulters_count || 0} Students</span>
          </div>
        </div>

        {/* Monthly Collection Ledger Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-900 text-base">Monthly Revenue Breakdown ({selectedYear})</h2>
            <span className="text-xs text-slate-400 font-semibold">{data?.monthly_breakdown?.length || 0} Months Logged</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-sans">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase">
                  <th className="py-3.5 px-6">Month</th>
                  <th className="py-3.5 px-6 text-center">Total Bills</th>
                  <th className="py-3.5 px-6 text-right">Collected (Rs.)</th>
                  <th className="py-3.5 px-6 text-right">Pending (Rs.)</th>
                  <th className="py-3.5 px-6 text-center">Collection %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800 font-medium">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-slate-400 font-medium">Calculating financial metrics...</td>
                  </tr>
                ) : !data?.monthly_breakdown || data.monthly_breakdown.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-slate-400 font-medium">No fee collection logs found for {selectedYear}.</td>
                  </tr>
                ) : (
                  data.monthly_breakdown.map((r) => {
                    const collected = parseFloat(r.collected_amount || 0);
                    const pending = parseFloat(r.pending_amount || 0);
                    const total = collected + pending;
                    const pct = total > 0 ? Math.round((collected / total) * 100) : 100;
                    return (
                      <tr key={r.month} className="hover:bg-slate-50/80 transition">
                        <td className="py-3.5 px-6 font-bold text-slate-900 text-sm">{monthNames[r.month]} {r.year}</td>
                        <td className="py-3.5 px-6 text-center font-bold text-slate-900">{r.total_bills}</td>
                        <td className="py-3.5 px-6 text-right font-mono font-bold text-emerald-600">Rs. {collected.toLocaleString(undefined, { minimumFractionDigits: 0 })}</td>
                        <td className="py-3.5 px-6 text-right font-mono font-bold text-red-600">Rs. {pending.toLocaleString(undefined, { minimumFractionDigits: 0 })}</td>
                        <td className="py-3.5 px-6 text-center font-bold text-blue-600">{pct}%</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};

export default FeeReportPage;
